import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageProvider';
import { localeTagFor } from '@/i18n/locale';
import { useProactiveIdentity } from '@/hooks/useProactiveIdentity';
import { useProactiveHomeSummary } from '@/hooks/useProactiveHomeSummary';
import type { ProactiveGuard } from '@/lib/proactiveAssistant';

const retryButton = 'min-h-11 rounded-xl border border-border px-4 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background';

function HomeBriefingShell({ children }: { children: ReactNode }) {
  const { t } = useLanguage();
  return (
    <section aria-label={t('proactive.home.title')}
      className='mb-4 space-y-3 rounded-2xl border border-primary/25 bg-card p-4 text-foreground sm:p-5'>
      <div className='flex min-w-0 items-center gap-2'>
        <Sparkles size={18} aria-hidden='true' className='shrink-0 text-primary' />
        <h2 className='min-w-0 break-words font-display text-base font-semibold'>
          {t('proactive.home.title')}
        </h2>
      </div>
      {children}
      <p className='text-xs leading-relaxed text-muted-foreground'>{t('proactive.home.reviewHint')}</p>
      <Link to='/briefing'
        className='flex min-h-11 w-full items-center justify-between gap-3 rounded-xl bg-primary/10 px-4 py-3 text-sm font-medium text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background'>
        <span className='min-w-0 break-words'>{t('proactive.home.open')}</span>
        <ArrowRight size={16} aria-hidden='true' className='shrink-0' />
      </Link>
    </section>
  );
}

export function ProactiveHomeCard() {
  const identity = useProactiveIdentity(), { t } = useLanguage();
  if (!identity.ready) return <HomeBriefingShell><p role='status'>{t('proactive.loading')}</p></HomeBriefingShell>;
  if (identity.error || !identity.userId || !identity.guard || !identity.guard.isCurrent()) {
    return <HomeBriefingShell>
      <p role='alert' className='text-sm'>{t('proactive.error')}</p>
      <button className={retryButton} onClick={identity.retry}>{t('common.retry')}</button>
    </HomeBriefingShell>;
  }
  return <OwnedProactiveHomeCard
    key={identity.userId + ':' + identity.guard.authSessionId + ':' + identity.generation}
    owner={identity.userId} guard={identity.guard} />;
}

export function OwnedProactiveHomeCard({ owner, guard }: { owner: string; guard: ProactiveGuard }) {
  const { t, tf, tp, lang } = useLanguage();
  const { status, summary, retry } = useProactiveHomeSummary(owner, guard);
  const time = summary?.generatedAt === null || !summary ? null : new Intl.DateTimeFormat(
    localeTagFor(lang), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
  ).format(summary.generatedAt);
  return <HomeBriefingShell>
    {status === 'loading' && <p role='status' className='text-sm text-muted-foreground'>
      {t('proactive.loading')}
    </p>}
    {(status === 'error' || status === 'disabled') && <>
      <p role='alert' className='text-sm'>{t(status === 'disabled' ? 'proactive.disabled' : 'proactive.error')}</p>
      <button className={retryButton} onClick={retry}>{t('common.retry')}</button>
    </>}
    {status === 'ready' && summary && <div className='space-y-2'>
      <p className='text-xs font-medium text-muted-foreground'>
        {t('proactive.home.control.' + summary.control)}
      </p>
      {summary.control === 'off'
        ? <p className='text-sm leading-relaxed'>{t('proactive.home.setup')}</p>
        : <>
          <p role='status' className='break-words text-sm font-medium leading-relaxed'>
            {summary.cache === 'prepared'
              ? tp('proactive.home.prepared', summary.expiresAt.length)
              : t('proactive.home.' + summary.cache)}
          </p>
          {time && summary.generatedAt !== null && <time className='block text-xs text-muted-foreground'
            dateTime={new Date(summary.generatedAt).toISOString()}>
            {tf('proactive.home.generated', { time })}
          </time>}
          {summary.origin && summary.cache === 'prepared' && <p className='text-xs text-muted-foreground'>
            {t('proactive.home.origin.' + summary.origin)}
          </p>}
        </>}
    </div>}
  </HomeBriefingShell>;
}
